/**
 * <Highlight> — le surligneur (atom, Brand core V16 chap. 03 et 06).
 *
 * « Le fluo passe sur l'encre » : le jaune ne se pose jamais seul sur la feuille, il souligne un
 * mot. Sur fond clair, une bande `#FFE000` passe sous le BAS du mot, légèrement inclinée ; sur la
 * nuit, elle couvre le mot entier et le texte passe en marine. Un mot, une information : l'heure
 * de la prochaine séance, une remise — jamais une phrase, jamais un titre entier.
 *
 * En code : une vue jaune posée en absolu DERRIÈRE le texte (la recette `.hl` du kit, dans
 * `tokens/effects` → `highlighter`). Le mot ne se coupe pas : il est un seul bloc, qui passe à la
 * ligne d'un tenant.
 *
 * Avec `<Logo>`, le seul dessin qui demande au thème s'il est sombre : ce n'est pas une couleur
 * qui change, c'est la FORME — la bande monte couvrir le mot (`useHighlighterBand`, partagé avec
 * l'énoncé). L'encre qui en découle n'est pas choisie ici : posé sur l'aplat jaune, le mot prend
 * l'encre de cet aplat.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { highlighter, type HighlighterBand } from "../../theme/tokens/effects"
import { type FontFamilyKey, type FontSizeKey } from "../../theme/tokens/typography"
import { Text } from "../typography/Text"
import { useHighlighterBand } from "./useHighlighterBand"

export type HighlightProps = {
    /** Le mot surligné. */
    children: string
    /** Taille du mot — celle du texte qui l'entoure. `text-base` par défaut. */
    size?: FontSizeKey
    /** Graisse du mot — celle du texte qui l'entoure. `bold` par défaut. */
    weight?: FontFamilyKey
    /** L'encre du mot sur fond clair ; sur la nuit, c'est celle de l'aplat jaune. `text-primary` par défaut. */
    color?: SemanticColorKey
    testID?: string
}

export function Highlight(props: HighlightProps) {
    const { children, size = "text-base", weight = "bold", color = "text-primary", testID } = props
    const { band, ink } = useHighlighterBand()

    return (
        <View testID={testID} style={styles.word(size, band)}>
            <View style={styles.band(size, band)} pointerEvents="none" />
            <Text size={size} weight={weight} color={ink ?? color} numberOfLines={1}>
                {children}
            </Text>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    word: (size: FontSizeKey, band: HighlighterBand) => ({
        paddingHorizontal: band.padX * theme.fontSizes[size],
    }),
    band: (size: FontSizeKey, band: HighlighterBand) => {
        const em = theme.fontSizes[size]
        return {
            position: "absolute" as const,
            left: band.insetX * em,
            right: band.insetX * em,
            top: typeof band.top === "number" ? band.top * em : band.top,
            bottom: band.bottom * em,
            borderRadius: highlighter.radius * em,
            backgroundColor: theme.colors.accent,
            transform: [{ rotate: `${highlighter.rotate}deg` }],
        }
    },
}))
